import { version } from './version';
import getEnvValue from '@/joystagram-components/commons/getEnvValue';

const SENTRY_ENVIRONMENT_LOCAL = 'local';
const SENTRY_ENVIRONMENT_UNKNOWN = 'unknown';

const sentryEnvironment =
  getEnvValue('NEXT_PUBLIC_ENV_NAME') ?? SENTRY_ENVIRONMENT_UNKNOWN;

const isProduction =
  sentryEnvironment?.includes('prod') || sentryEnvironment?.includes('stg');
const isLocal = sentryEnvironment === SENTRY_ENVIRONMENT_LOCAL;

const isLocalSentryEnabled =
  getEnvValue('NEXT_PUBLIC_SENTRY_LOCAL_ENABLED') === 'true';

const isSentryDebuggingEnabled =
  getEnvValue('NEXT_PUBLIC_SENTRY_DEBUGGING_ENABLED') === 'true';

const sentryDsn = getEnvValue('NEXT_PUBLIC_SENTRY_DSN');

// True if any of the last N breadcrumbs is a failed fetch/xhr to `hostMatch`.
// Used to corroborate that an exception came from a specific vendor SDK.
const hasRecentFailedNetworkCall = (event, hostMatch, lookback = 5) => {
  const breadcrumbs = event?.breadcrumbs?.values || event?.breadcrumbs || [];
  return breadcrumbs.slice(-lookback).some(bc => {
    if (bc?.category !== 'fetch' && bc?.category !== 'xhr') return false;
    if (!(bc?.data?.url || '').includes(hostMatch)) return false;
    const status = bc?.data?.status_code;
    return status === 0 || status === undefined || status >= 400;
  });
};

// True if any exception frame's filename contains `pattern`. Used to scope a
// filter to a specific vendor SDK so future first-party throws aren't silenced.
const hasFrameMatching = (event, pattern) =>
  (event?.exception?.values || []).some(exc =>
    (exc?.stacktrace?.frames || []).some(f =>
      (f?.filename || '').includes(pattern)
    )
  );

// CON-8174 — Cylindo 3D-viewer SDK fails its config XHR (network drop / blocker).
// Corroborated by a failed content.cylindo.com call in recent breadcrumbs.
const CYLINDO_HOST = 'content.cylindo.com';
const isCylindoAxiosError = (error, event) => {
  if (
    error?.name === 'AxiosError' &&
    error?.message === 'Network Error' &&
    hasRecentFailedNetworkCall(event, CYLINDO_HOST)
  ) {
    return true;
  }
  if (error?.name !== 'TypeError' || typeof error?.message !== 'string') {
    return false;
  }
  const msg = error.message;
  return (
    msg.includes(CYLINDO_HOST) &&
    (msg.includes('Failed to fetch') ||
      msg.includes('Load failed') ||
      msg.includes('NetworkError'))
  );
};

// CON-8175 — Pinterest conversion tracker blocked by ad-blockers. Match the
// three browser phrasings, scoped to the Pinterest host.
const PINTEREST_HOST = 'ct.pinterest.com';
const isPinterestFetchFailure = error => {
  if (error?.name !== 'TypeError' || typeof error?.message !== 'string') {
    return false;
  }
  const msg = error.message;
  return (
    msg.includes(PINTEREST_HOST) &&
    (msg.includes('Failed to fetch') ||
      msg.includes('Load failed') ||
      msg.includes('NetworkError'))
  );
};

// CON-8176 — OneTrust otSDKStub.js null-derefs `.Domain` after its geolocation
// XHR fails. Frame check scopes this to OneTrust so first-party `.Domain`
// access throwing the same message in the future would still reach Sentry.
const isOneTrustDomainError = (error, event) =>
  error?.name === 'TypeError' &&
  typeof error?.message === 'string' &&
  error.message.includes("reading 'Domain'") &&
  hasFrameMatching(event, 'otSDKStub.js');

const sharedSentryConfig = {
  dsn: sentryDsn,
  // By default Sentry is only enabled in non-local environments, but to enable it locally for debugging
  // we can set an environment variable in .env.local: NEXT_PUBLIC_SENTRY_LOCAL_ENABLED=true
  enabled: !!sentryDsn && (!isLocal || isLocalSentryEnabled),
  environment: sentryEnvironment,
  release: version,

  // Performance monitoring - configurable via environment variable.
  // Shared control across Sentry, server-side OTel, and the Dash0 Web SDK gate
  // in instrumentation-client.js (see .github/instructions/tracing.instructions.md).
  tracesSampleRate: parseFloat(
    getEnvValue('NEXT_PUBLIC_TRACING_SAMPLE_RATE') || '0.001'
  ),

  // Enhanced error tracking with filtering
  beforeSend(event, hint) {
    if (isProduction && event.exception) {
      const error = hint.originalException;
      if (error?.message?.includes('Non-Error promise rejection')) {
        return null;
      }
      if (
        isCylindoAxiosError(error, event) ||
        isPinterestFetchFailure(error) ||
        isOneTrustDomainError(error, event)
      ) {
        return null;
      }
    }
    return event;
  },

  // Debugging - only when explicitly enabled
  debug: isSentryDebuggingEnabled,

  // Attach request headers and IP for users, for better debugging
  sendDefaultPii: true,

  maxBreadcrumbs: 30,
  attachStacktrace: true,
};

export default sharedSentryConfig;
